import { AxiosError } from 'axios';
import produce, { enableES5 } from 'immer';
import { useEffect, useState } from 'react';
import { GenericErrorResponse } from '../../../api';
import {
  PutUpdateAccessRoleRequest,
  usePutUpdateAccessRole,
} from '../../../api-refactored/user/usePostUpdateAccessRole';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import {
  AccessMatrixRoleConfiguration,
  LookupListItem,
  RoleAccessUpdates,
} from '../../../api/user/model';

enableES5();

const isSameUpdateItem = (element: RoleAccessUpdates, update: RoleAccessUpdates) =>
  element.moduleId === update.moduleId && element.roleId === update.roleId;

export interface UseRoleAccessConfigDataReturn {
  /**
   * Access Matrix data to be displayed at access matrix table
   */
  accessMatrix: AccessMatrixRoleConfiguration[];
  /**
   * Function to add role to access matrix table
   */
  onClickAddRole: (roleId: number) => void; // previously onClickAddInstitution
  /**
   * Function to remove role from access matrix table
   */
  onClickRemoveRole: (roleId: number) => void; // previously onClickRemoveInstitution
  /**
   * Function to submit all current changes on the access matrix table
   */
  onClickSubmit: () => void;
  /**
   * Function to toggle access checkbox in the table
   */
  onClickToggleAccess: (roleId: number, moduleId: number) => void;
  /**
   * React query mutation object
   */
  accessConfigMutator: UseCustomMutationReturn<
    any,
    AxiosError<GenericErrorResponse>,
    PutUpdateAccessRoleRequest,
    unknown
  >;
  /**
   * Whether the user has made any changes to the access table
   */
  hasUserMadeChanges: boolean;
}

/**
 * A hook that returns:
 * 1. access matrix that should be used by the access matrix table
 * 2. functions that should be called to update the access matrix
 * 3. other access matrix related data e.g. hasUserMadeChanges etc.
 *
 * @param accessMatrixConfiguration accessMatrixConfiguration from GET API
 * @returns
 */
const useRoleAccessConfigData = (
  accessMatrixConfiguration?: AccessMatrixRoleConfiguration[]
): UseRoleAccessConfigDataReturn => {
  const accessConfigMutator = usePutUpdateAccessRole({
    onSuccess: () => {
      setUpdates([]);
    },
  });

  const [accessMatrix, setAccessMatrix] = useState<AccessMatrixRoleConfiguration[]>([]);
  const [updates, setUpdates] = useState<RoleAccessUpdates[]>([]);

  useEffect(() => {
    if (accessMatrixConfiguration) {
      setAccessMatrix(accessMatrixConfiguration);
    } else {
      setAccessMatrix([]);
    }
  }, [accessMatrixConfiguration]);

  const onClickToggleAccess = (roleId: number, moduleId: number) => {
    const update: RoleAccessUpdates = {
      roleId,
      moduleId,
      isInsert: false,
    };

    const newAccessMatrix = produce(accessMatrix, (draft) => {
      const accessRightsOfRoleId = draft.findIndex((role) => role.id === roleId); // assume this constant will never be undefined else onClickToggleAccess will not be triggered
      const indexOfModuleId = draft[accessRightsOfRoleId].modules.indexOf(moduleId);
      if (indexOfModuleId > -1) {
        draft[accessRightsOfRoleId].modules.splice(indexOfModuleId, 1);
      } else {
        draft[accessRightsOfRoleId].modules.push(moduleId);
      }
    });

    // since is a binary operation if it something already exist in the update array that means
    // on that this time the update is reverted so can just delete from list of updates
    const index = updates.findIndex((element) => isSameUpdateItem(element, update));

    if (index > -1) {
      const newUpdates = [...updates];
      newUpdates.splice(index, 1);
      setUpdates(newUpdates);
    } else {
      setUpdates([...updates, update]);
    }

    setAccessMatrix(newAccessMatrix);
  };

  const onClickAddRole = (roleId: number) => {
    setAccessMatrix(
      produce(accessMatrix, (draft) => {
        draft.push({ id: roleId, modules: [] });
      })
    );
  };

  const onClickRemoveRole = (roleId: number) => {
    setAccessMatrix(
      produce(accessMatrix, (draft) => {
        const accessRightsOfRoleId = draft.findIndex((role) => role.id === roleId); // assume this constant will never be undefined else onClickRemoveRole will not be triggered
        const updateState = [...updates];

        // for this we loop through the stuff and update the thing accordingly before
        // updating state cause if we update the state via loop will cause issues
        // like only the last entry in the loop will be updated
        for (let i = 0; i < accessMatrix[accessRightsOfRoleId].modules.length; i += 1) {
          const update: RoleAccessUpdates = {
            roleId,
            moduleId: accessMatrix[accessRightsOfRoleId].modules[i],
            isInsert: false,
          };
          // since is a binary operation if it something already exist in the update array that means
          // on that this time the update is reverted so can just delete from list of updates
          const index = updateState.findIndex((element) => isSameUpdateItem(element, update));
          if (index > -1) {
            updateState.splice(index, 1);
          } else {
            updateState.push(update);
          }
        }

        draft.splice(accessRightsOfRoleId, 1);

        setUpdates(updateState);
      })
    );
  };

  const onClickSubmit = () => {
    // TODO - alex: to delete if logic does not need changing
    // Transformation to update api
    // const updateRequest: UpdateAccessMatrixRequest = {
    //   updatedRoles: [],
    // };
    // for (let i = 0; i < updates.length; i += 1) {
    //   const x = accessMatrix.find((e) => e.roleId === updates[i].roleId);
    //   const updateItem: UpdateAccessMatrixRequestListItem = {
    //     roleId: updates[i].roleId,
    //     // modules: accessMatrix[updates[i].roleId] || [],
    //     modules: x?.modules || [],
    //   };
    //   if (updateRequest.updatedRoles.findIndex((e) => e.roleId === updateItem.roleId) < 0) {
    //     updateRequest.updatedRoles.push(updateItem);
    //   }
    // }

    accessConfigMutator.mutate({ updatedRoles: accessMatrix });
  };

  return {
    accessMatrix,
    onClickToggleAccess,
    onClickAddRole,
    onClickRemoveRole,
    onClickSubmit,
    accessConfigMutator,
    hasUserMadeChanges: updates.length > 0,
  };
};

export interface UseRoleListReturn {
  /**
   * Contains a list of roles that could be added to access matrix table
   */
  availableRolesToAdd: LookupListItem[];
  /**
   * Contains a list of roles that could be deleted from the access matrix table
   */
  availableRolesToDelete: LookupListItem[];
}

/**
 * Separate "availableRoles" array into "availableRolesToAdd" array and "availableRolesToDelete" array based on what
 * role is currently at the access matrix table. For example, if roleId "1" is in the access matrix, roleId "1" will be
 * in "availableRolesToDelete" array. User can choose any role in the "availableRolesToDelete" array to remove the
 * role's entire row from access matrix table.
 *
 * @param accessMatrix the current access matrix data
 * @param availableRoles all available roles, should be from lookup API
 */
const useRoleList = (
  accessMatrix: AccessMatrixRoleConfiguration[],
  availableRoles?: LookupListItem[]
): UseRoleListReturn => {
  const [availableRolesToAdd, setAvailableRolesToAdd] = useState<LookupListItem[]>([]);
  const [availableRolesToDelete, setAvailableRolesToDelete] = useState<LookupListItem[]>([]);

  useEffect(() => {
    const toAddItems: LookupListItem[] = [];
    const toDeleteItems: LookupListItem[] = [];

    if (availableRoles) {
      availableRoles.forEach((availableRole) => {
        if (accessMatrix.find((role) => role.id === availableRole.id)) {
          toDeleteItems.push(availableRole);
        } else {
          toAddItems.push(availableRole);
        }
      });
    }

    setAvailableRolesToDelete(toDeleteItems);
    setAvailableRolesToAdd(toAddItems);
  }, [availableRoles, accessMatrix]);

  return {
    availableRolesToAdd,
    availableRolesToDelete,
  };
};

export { useRoleAccessConfigData, useRoleList };
